import React from "react";
import { StatusIndicator } from "@amzn/awsui-components-react";

interface Props {
  // Shown in the console log so the failing section is identifiable in a bug report.
  section: string;
  // React 18 types no longer include `children` implicitly on class components.
  children?: React.ReactNode;
}

interface State {
  hasError: boolean;
}

/**
 * Contains a render failure to one section of the page instead of blanking the whole app.
 *
 * The summary panel is assembled from many independent widgets built out of raw transaction
 * data, so one widget hitting unexpected data (a marketplace we have no realm for, a missing
 * document field) should degrade that widget only — the rest of the transaction is still
 * useful for an investigation.
 */
export default class SectionErrorBoundary extends React.Component<
  Props,
  State
> {
  state: State = { hasError: false };

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error(
      `Error rendering section "${this.props.section}": `,
      error,
      errorInfo
    );
  }

  render() {
    if (this.state.hasError) {
      return <StatusIndicator type="warning">Failed to render</StatusIndicator>;
    }

    return this.props.children;
  }
}
